import { useState } from 'react';
import { AudioLines } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

import { SignInForm } from '../components/SignInForm';
import { EditionPicker } from '../components/EditionPicker';
import { PersonalSignupForm } from '../components/PersonalSignupForm';
import { ProfessionalSignupForm } from '../components/ProfessionalSignupForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type Step = { kind: 'signin' } | { kind: 'pick-edition' } | { kind: 'signup'; edition: 'personal' | 'professional' };

const DEFAULT_NEXT_ROUTE = '/patients' as const;

const DESCRIPTIONS: Record<string, string> = {
  signin: 'Sign in to your account',
  'pick-edition': 'Create a new account',
  'signup:professional': 'Create a clinic account',
  'signup:personal': 'Create a personal health record',
};

export function LoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>({ kind: 'signin' });

  const goSuccess = () => navigate({ to: DEFAULT_NEXT_ROUTE });

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <AudioLines className="h-6 w-6" />
          </div>
          <CardTitle className="text-2xl">ConsultNow</CardTitle>
          <CardDescription>{describe(step)}</CardDescription>
        </CardHeader>
        <CardContent>
          {step.kind === 'signin' && (
            <SignInForm onSuccess={goSuccess} onCreateAccount={() => setStep({ kind: 'pick-edition' })} />
          )}
          {step.kind === 'pick-edition' && (
            <EditionPicker
              onBack={() => setStep({ kind: 'signin' })}
              onChoose={(edition) => setStep({ edition, kind: 'signup' })}
            />
          )}
          {step.kind === 'signup' && step.edition === 'personal' && (
            <PersonalSignupForm
              onSuccess={goSuccess}
              onSignIn={() => setStep({ kind: 'signin' })}
              onBack={() => setStep({ kind: 'pick-edition' })}
            />
          )}
          {step.kind === 'signup' && step.edition === 'professional' && (
            <ProfessionalSignupForm
              onSuccess={goSuccess}
              onSignIn={() => setStep({ kind: 'signin' })}
              onBack={() => setStep({ kind: 'pick-edition' })}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function describe(step: Step): string {
  if (step.kind === 'signup') return DESCRIPTIONS[`signup:${step.edition}`];
  return DESCRIPTIONS[step.kind];
}
